import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PostTossMaterialAcquisitionStatusesBody, ProductionMasters, TossMaterial } from '../../../../api/model';
import {
  useGetTossMaterialTree,
  usePostTossMaterialAcquisitionStatuses,
  usePostTossMaterialCategoryCancel,
  usePostTossMaterialCategoryLock,
  usePostTossMaterialCategorySave,
} from '../../../../api/toss-materials/toss-materials';
import { useDefaultOptions } from '../../../../hooks/api';
import { postWithKeepAlive } from '../../../../utils/api';
import { getTossTabName } from '../../../../utils/productions';
import ErrorComponent from '../../../atoms/ErrorComponent';
import NoDataComponent from '../../../atoms/NoDataComponent';
import WaitComponent from '../../../atoms/WaitComponent';
import AnotherTabLockingErrorModal from '../../../modules/AnotherTabLockingErrorModal';
import ConfirmModal from '../../../modules/ConfirmModal';
import ErrorModal from '../../../modules/ErrorModal';
import OkModal from '../../../modules/OkModal';
import SpinnerModal from '../../../modules/SpinnerModal';
import AcquisitionStatusesTab, { AcquisitionStatusesHandler } from './toss/AcquisitionStatusesTab';
import BomTab, { BomTabHandler } from './toss/BomTab';
import FListTab, { FListTabHandler } from './toss/FListTab';
import Master0Tab, { Master0TabHandler } from './toss/Master0Tab';
import ServiceSpecTab, { ServiceSpecTabHandler } from './toss/ServiceSpecTab';

interface Props {
  productionId: number;
  masters: ProductionMasters;
  editModeOuterTabIndex: number | undefined;
  setEditable: (editing: boolean) => void;
}

export interface ProductionTossMaterialsTabHandler {
  submit: () => Promise<void>;
  refresh: () => void;
}

/**
 * トス資料タブのコンポーネント
 */
const ProductionTossMaterialsTab = forwardRef<ProductionTossMaterialsTabHandler, Props>(
  ({ productionId, masters, editModeOuterTabIndex, setEditable }, ref) => {
    // 言語情報
    const {
      t,
      i18n: { language },
    } = useTranslation();

    // トス資料データの取得
    const options = useDefaultOptions(language);
    const { isLoading, isError, error, data, refetch, isFetching } = useGetTossMaterialTree(productionId, options);

    // 子タブの Ref
    const refMaster0Tab = useRef<Master0TabHandler>(null);
    const refServiceSpecTab = useRef<ServiceSpecTabHandler>(null);
    const refFListTab = useRef<FListTabHandler>(null);
    const refBomTab = useRef<BomTabHandler>(null);
    const refAcquisitionTab = useRef<AcquisitionStatusesHandler>(null);

    // フェッチが完了したら、画面をリフレッシュ
    useEffect(() => {
      if (!isFetching) {
        refMaster0Tab.current?.refresh();
        refServiceSpecTab.current?.refresh();
        refFListTab.current?.refresh();
        refBomTab.current?.refresh();
        refAcquisitionTab.current?.refresh();
      }
    }, [isFetching]);

    // 選択されているタブ（内側）
    const [selectedTabIndex, setSelectedTabIndex] = useState(1);

    // 編集モードのタブ（内側）
    const refEditModaInnerTabIndex = useRef<number>();
    const [editModeInnerTabIndex, _setEditModeTabIndex] = useState<number>();

    // 外側のタブと内側のタブの編集状態を一緒に変更する
    const setEditModeTabIndex = useCallback(
      (tabIndex: number | undefined) => {
        setEditable(tabIndex != null);
        _setEditModeTabIndex(tabIndex);
        refEditModaInnerTabIndex.current = tabIndex;
      },
      [setEditable, _setEditModeTabIndex]
    );

    // スピナーモーダルを表示する
    const [displaySpinnerModal, setDisplaySpinnerModal] = useState(false);

    // ロックを取得する setInterval の返す ID
    const refLockIntervalId = useRef<NodeJS.Timer>();
    useEffect(() => () => clearInterval(refLockIntervalId.current), []);

    // beforeUnloadEvent（ブラウザを閉じようとしたときに、メッセージ表示）
    const beforeUnload = useCallback((e: BeforeUnloadEvent) => {
      e.returnValue = '';
    }, []);

    // コンポーネントが表示されなくなる時に、beforeunload イベントの解除
    useEffect(() => () => window.removeEventListener('beforeunload', beforeUnload), [beforeUnload]);

    // unload 関数（ブラウザを閉じるときに発生して、ロック解除するためのもの）
    const unload = useCallback(
      async (tabIndex: number) => {
        await postWithKeepAlive(`/tossMaterials/${productionId}/categories/${tabIndex}/cancel`, language);
      },
      [productionId, language]
    );

    const refUnload = useRef<() => Promise<void>>();

    // コンポーネントが表示されなくなる時に、unload イベントの実行と解除
    useEffect(
      () => () => {
        if (refUnload.current != null) {
          void refUnload.current().then(() => {
            if (refUnload.current != null) {
              window.removeEventListener('unload', refUnload.current);
            }
          });
        }
      },
      []
    );

    // #region 保存関連

    // 保存 API
    const { mutateAsync: saveAsync, error: saveError } = usePostTossMaterialCategorySave(options);

    // 保存時エラーを表示するかどうか
    const [displaySaveError, setDisplaySaveError] = useState(false);

    // 保存完了メッセージを表示するかどうか
    const [displayComleteSavingMessage, setDisplayCompleteSavingMessage] = useState(false);

    // 保存時エラーが設定された場合は表示
    useEffect(() => {
      setDisplaySaveError(saveError != null);
      setDisplaySpinnerModal(false);
    }, [saveError]);

    // 保存成功時
    const onSuccessSave = useCallback(async () => {
      // ブラウザを閉じた時などのイベントを解除
      window.removeEventListener('beforeunload', beforeUnload);
      if (refUnload.current != null) {
        window.removeEventListener('unload', refUnload.current);
      }

      try {
        // ロック API の呼び出しを解除
        clearInterval(refLockIntervalId.current);

        // 保存成功したらリフェッチする
        await refetch();

        // 編集状態を解除
        setEditModeTabIndex(undefined);

        // 保存完了メッセージ
        setDisplayCompleteSavingMessage(true);
      } catch {
        // Do nothing
      } finally {
        // スピナーモーダル非表示
        setDisplaySpinnerModal(false);
      }
    }, [beforeUnload, refetch, setEditModeTabIndex]);

    // 保存失敗時
    const onErrorSave = useCallback(() => setDisplaySpinnerModal(false), [setDisplaySpinnerModal]);

    // 保存する
    const save = useCallback(
      async (tabIndex: number, tossMaterialList: TossMaterial[]) => {
        // 保存実行
        await saveAsync(
          {
            productionId,
            tossMaterialCategoryId: tabIndex,
            data: { toss_materials: tossMaterialList },
          },
          { onSuccess: onSuccessSave, onError: onErrorSave }
        );
      },
      [saveAsync, productionId, onSuccessSave, onErrorSave]
    );

    // 取得率保存 API
    const { mutateAsync: saveAcqusitionAsync, error: saveAcqusitionError } =
      usePostTossMaterialAcquisitionStatuses(options);

    // 取得率保存時エラーを表示するかどうか
    const [displaySaveAcqusitionError, setDisplaySaveAcqusitionError] = useState(false);

    // 取得率保存時エラーが設定された場合は表示
    useEffect(() => {
      setDisplaySaveAcqusitionError(saveAcqusitionError != null);
      setDisplaySpinnerModal(false);
    }, [saveAcqusitionError]);

    // 取得率保存
    const saveAcqusition = useCallback(
      async (saveData: PostTossMaterialAcquisitionStatusesBody) => {
        await saveAcqusitionAsync({ productionId, data: saveData }, { onSuccess: onSuccessSave, onError: onErrorSave });
      },
      [onErrorSave, onSuccessSave, productionId, saveAcqusitionAsync]
    );

    // #endregion

    // #region ロック関連

    // ロック API
    const { mutateAsync: lockAsync, error: lockError } = usePostTossMaterialCategoryLock(options);

    // ロックエラーを表示するかどうか
    const [displayLockError, setDisplayLockError] = useState(false);

    // ロックエラーが設定された場合は表示
    useEffect(() => {
      setDisplayLockError(lockError != null);
      setDisplaySpinnerModal(false);
    }, [lockError]);

    // 他のタブ（外側）がロック中であるエラーを表示
    const [displayAnotherOuterTabLockingError, setDisplayAnotherOuterTabLockingError] = useState(false);

    // 他のタブ（内側）がロック中であるエラー表示
    const [displayAnotherInnerTabLockingError, setDisplayAnotherInnerTabLocking] = useState(false);

    // 編集キャンセルした場合の処理
    const finishEditing = useCallback(() => {
      // ロックの定期実行をやめる
      clearInterval(refLockIntervalId.current);

      // 編集モードをやめる
      setEditModeTabIndex(undefined);

      // ブラウザを閉じた時などのイベントを解除
      window.removeEventListener('beforeunload', beforeUnload);
      if (refUnload.current != null) {
        window.removeEventListener('unload', refUnload.current);
      }

      // 子タブをリフレッシュ
      refMaster0Tab.current?.refresh();
      refServiceSpecTab.current?.refresh();
      refFListTab.current?.refresh();
      refBomTab.current?.refresh();
      refAcquisitionTab.current?.refresh();

      // スピナーモーダルを閉じる
      setDisplaySpinnerModal(false);
    }, [beforeUnload, setEditModeTabIndex]);

    // 編集モードにする
    const edit = useCallback(
      async (tabIndex: number) => {
        // 既に編集モードの時は何もしない
        if (editModeInnerTabIndex === tabIndex) return;

        // 他のタブ（内側）が編集中の場合はエラー表示
        if (editModeInnerTabIndex != null) {
          setDisplayAnotherInnerTabLocking(true);
          return;
        }

        // 他のタブ（外側）が編集中の場合はエラー表示
        if (editModeOuterTabIndex != null && editModeOuterTabIndex !== 3) {
          setDisplayAnotherOuterTabLockingError(true);
          return;
        }

        // スピナーモーダルの表示
        setDisplaySpinnerModal(true);

        // 成功した場合の処理
        const onSuccess = async () => {
          try {
            // 表示するデータをリフェッチする
            await refetch();

            // 定期的にロック API をたたく
            const lockInterval = parseInt(process.env.REACT_APP_GET_LOCK_INTERVAL ?? '10000', 10);
            refLockIntervalId.current = setInterval(() => {
              void lockAsync({ productionId, tossMaterialCategoryId: tabIndex }, { onError: finishEditing });
            }, lockInterval);

            // ブラウザを閉じた時などのロック解除を設定
            window.addEventListener('beforeunload', beforeUnload);
            refUnload.current = () => unload(tabIndex);
            window.addEventListener('unload', refUnload.current);

            // 編集中に設定する
            setEditModeTabIndex(tabIndex);
          } catch {
            // Do nothing
          } finally {
            setDisplaySpinnerModal(false);
          }
        };

        // 成功失敗共通の処理（スピナーモーダルを非表示に）
        const onError = () => setDisplaySpinnerModal(false);

        // ロック API にアクセス
        await lockAsync({ productionId, tossMaterialCategoryId: tabIndex }, { onSuccess, onError });
      },
      [
        beforeUnload,
        editModeInnerTabIndex,
        editModeOuterTabIndex,
        finishEditing,
        lockAsync,
        productionId,
        refetch,
        setEditModeTabIndex,
        unload,
      ]
    );

    // #endregion

    // #region キャンセル関連

    // キャンセル API
    const { mutateAsync: cancelAsync, error: cancelError } = usePostTossMaterialCategoryCancel(options);

    // キャンセルエラーを表示するかどうか
    const [displayCancelError, setDisplayCancelError] = useState(false);

    // キャンセルエラーが設定された場合は表示
    useEffect(() => {
      setDisplayCancelError(cancelError != null);
      setDisplaySpinnerModal(false);
    }, [cancelError]);

    // 編集モードをキャンセル
    const cancel = useCallback(
      async (tabIndex: number) => {
        // スピナーモーダルの表示
        setDisplaySpinnerModal(true);

        // 成功失敗共通の処理（スピナーモーダルを非表示に）
        const onError = () => setDisplaySpinnerModal(false);

        // ロックキャンセル API をたたく
        await cancelAsync({ productionId, tossMaterialCategoryId: tabIndex }, { onSuccess: finishEditing, onError });
      },
      [cancelAsync, productionId, finishEditing]
    );

    // コンポーネントが非表示になったときにキャンセルする
    useEffect(
      () => () => {
        if (refEditModaInnerTabIndex.current != null) {
          void unload(refEditModaInnerTabIndex.current);
        }
      },
      [unload]
    );

    // #endregion

    // バリデーションエラーを表示するかどうか
    const [displayValidationError, setDisplayValidationError] = useState(false);

    // 確認メッセージの表示
    const [displayConfirmMessage, setDisplayConfirmMessage] = useState(false);

    // フォームの送信
    const submit = useCallback(async () => {
      let result: boolean | undefined;
      if (selectedTabIndex === 1) {
        setDisplaySpinnerModal(true);
        result = await refMaster0Tab.current?.validate();
      } else if (selectedTabIndex === 2) {
        setDisplaySpinnerModal(true);
        result = await refServiceSpecTab.current?.validate();
      } else if (selectedTabIndex === 3) {
        setDisplaySpinnerModal(true);
        result = await refFListTab.current?.validate();
      } else if (selectedTabIndex === 4) {
        setDisplaySpinnerModal(true);
        result = await refBomTab.current?.validate();
      } else if (selectedTabIndex === 5) {
        setDisplaySpinnerModal(true);
        result = await refAcquisitionTab.current?.validate();
      }

      setDisplaySpinnerModal(false);
      if (result != null) {
        if (result) {
          setDisplayConfirmMessage(true);
        } else {
          setDisplayValidationError(true);
        }
      }
    }, [selectedTabIndex]);

    // タブ全体のリフレッシュ
    const refresh = useCallback(() => {
      // データをリフェッチ
      void refetch();
    }, [refetch]);

    // 親コンポーネントから操作できるようにする
    useImperativeHandle(ref, () => ({
      submit,
      refresh,
    }));

    // 待ち状態
    if (isLoading) {
      return <WaitComponent />;
    }

    // エラー発生時
    if (isError) {
      if (error.response?.data.error_code === 'ProductionsNoPrivilege') {
        return (
          <div>
            <div style={{ fontSize: '14px' }}>{t('権限がないためトス資料タブを表示することができません')}</div>
          </div>
        );
      }
      return <ErrorComponent error={error} />;
    }

    // データが適切に取得できない場合
    if (
      data.data.production_id == null ||
      data.data.share_destination_productions == null ||
      data.data.toss_material_category_trees == null ||
      data.data.toss_material_acquisition_statuses == null
    ) {
      return <NoDataComponent />;
    }

    // 通常時
    return (
      <div className="tab-area2">
        {[1, 2, 3, 4, 5].map((x) => (
          <input
            key={x}
            id={`tab2-btn${x}`}
            className="tab-btn2"
            name="tab2"
            type="radio"
            checked={selectedTabIndex === x}
            onChange={(e) => {
              const target = e.target as HTMLInputElement;
              if (target.checked) {
                setSelectedTabIndex(x);
              }
            }}
          />
        ))}
        <ul className="tab-list-wrap2">
          {[t('0マスタ'), t('サービススペック'), t('F-List'), t('部品表'), t('トス資料取得率')].map((x, i) => {
            const index = i + 1;
            return (
              <li key={x.toString()}>
                <label
                  id={`tab2-list${index}`}
                  className={`tab-list2 ${selectedTabIndex === index ? 'current' : ''}`}
                  htmlFor={`tab2-btn${index}`}
                  // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.code === 'Space') {
                      // TODO: document.getElementById を使わないようにする
                      document.getElementById(`tab2-btn${index}`)?.click();
                      e.preventDefault();
                    }
                  }}
                >
                  {x}
                </label>
              </li>
            );
          })}
        </ul>
        <div className="tab-content-wrap2">
          {/* 0マスタタブ */}
          {data.data.toss_material_category_trees.length >= 1 && (
            <Master0Tab
              ref={refMaster0Tab}
              productionId={productionId}
              tree={data.data.toss_material_category_trees[0]}
              shareDestinationProductions={data.data.share_destination_productions}
              masters={masters}
              editable={editModeInnerTabIndex === 1}
              save={(tossMaterialList: TossMaterial[]) => save(1, tossMaterialList)}
              edit={() => edit(1)}
              cancel={() => cancel(1)}
              setDisplayValidationError={setDisplayValidationError}
            />
          )}
          {/* サービススペックタブ */}
          {data.data.toss_material_category_trees.length >= 2 && (
            <ServiceSpecTab
              ref={refServiceSpecTab}
              productionId={productionId}
              tree={data.data.toss_material_category_trees[1]}
              masters={masters}
              editable={editModeInnerTabIndex === 2}
              save={(tossMaterialList: TossMaterial[]) => save(2, tossMaterialList)}
              edit={() => edit(2)}
              cancel={() => cancel(2)}
              setDisplayValidationError={setDisplayValidationError}
            />
          )}
          {/* FList タブ */}
          {data.data.toss_material_category_trees.length >= 3 && (
            <FListTab
              ref={refFListTab}
              productionId={productionId}
              tree={data.data.toss_material_category_trees[2]}
              editable={editModeInnerTabIndex === 3}
              save={(tossMaterialList: TossMaterial[]) => save(3, tossMaterialList)}
              edit={() => edit(3)}
              cancel={() => cancel(3)}
              setDisplayValidationError={setDisplayValidationError}
            />
          )}
          {/* 部品表タブ */}
          {data.data.toss_material_category_trees.length >= 4 && (
            <BomTab
              ref={refBomTab}
              productionId={productionId}
              tree={data.data.toss_material_category_trees[3]}
              masters={masters}
              editable={editModeInnerTabIndex === 4}
              save={(tossMaterialList: TossMaterial[]) => save(4, tossMaterialList)}
              edit={() => edit(4)}
              cancel={() => cancel(4)}
              setDisplayValidationError={setDisplayValidationError}
            />
          )}
          {/* トス資料取得率タブ */}
          <AcquisitionStatusesTab
            ref={refAcquisitionTab}
            data={data.data.toss_material_acquisition_statuses}
            acqusitionStatuses={masters.toss_material_acquisition_statuses ?? []}
            editable={editModeInnerTabIndex === 5}
            edit={() => edit(5)}
            cancel={() => cancel(5)}
            save={saveAcqusition}
          />
        </div>
        {/* スピナーモーダル */}
        {displaySpinnerModal && <SpinnerModal />}
        {/* 保存時のエラー表示 */}
        {displaySaveError && <ErrorModal onClickOk={() => setDisplaySaveError(false)} error={saveError} />}
        {/* ロック時のエラー表示 */}
        {displayLockError && <ErrorModal onClickOk={() => setDisplayLockError(false)} error={lockError} />}
        {/* キャンセル時のエラー表示 */}
        {displayCancelError && <ErrorModal onClickOk={() => setDisplayCancelError(false)} error={cancelError} />}
        {/* 取得率保存時のエラー表示 */}
        {displaySaveAcqusitionError && (
          <ErrorModal onClickOk={() => setDisplaySaveAcqusitionError(false)} error={saveAcqusitionError} />
        )}
        {/* バリデーションエラーモーダル */}
        {displayValidationError && (
          <OkModal onClickOk={() => setDisplayValidationError(false)}>{t('バリデーションエラーです。')}</OkModal>
        )}
        {/* 他のタブ（外側）がロックされているエラーモーダル */}
        {displayAnotherOuterTabLockingError && editModeOuterTabIndex != null && (
          <AnotherTabLockingErrorModal
            editModeTabIndex={editModeOuterTabIndex}
            onClickOk={() => setDisplayAnotherOuterTabLockingError(false)}
          />
        )}
        {/* 他のタブ（外側）がロックされているエラーモーダル */}
        {displayAnotherInnerTabLockingError && editModeInnerTabIndex != null && (
          <OkModal onClickOk={() => setDisplayAnotherInnerTabLocking(false)}>
            {t('{{tab}}が編集中のため、このタブを編集できません。', {
              tab: getTossTabName(editModeInnerTabIndex, t),
            } as FieldValues).toString()}
          </OkModal>
        )}
        {/* 保存完了メッセージモーダル */}
        {displayComleteSavingMessage && (
          <OkModal onClickOk={() => setDisplayCompleteSavingMessage(false)}>
            <div>{t('保存しました。')}</div>
          </OkModal>
        )}
        {/* 保存確認メッセージ */}
        {displayConfirmMessage && (
          <ConfirmModal
            onClickOk={async () => {
              setDisplayConfirmMessage(false);
              if (editModeInnerTabIndex === 1) {
                setDisplaySpinnerModal(true);
                await refMaster0Tab.current?.submit();
              } else if (editModeInnerTabIndex === 2) {
                setDisplaySpinnerModal(true);
                await refServiceSpecTab.current?.submit();
              } else if (editModeInnerTabIndex === 3) {
                setDisplaySpinnerModal(true);
                await refFListTab.current?.submit();
              } else if (editModeInnerTabIndex === 4) {
                setDisplaySpinnerModal(true);
                await refBomTab.current?.submit();
              } else if (editModeInnerTabIndex === 5) {
                setDisplaySpinnerModal(true);
                await refAcquisitionTab.current?.submit();
              }
            }}
            onClickCancel={() => setDisplayConfirmMessage(false)}
          >
            <div>{t('データを更新しますか？')}</div>
          </ConfirmModal>
        )}
      </div>
    );
  }
);

export default ProductionTossMaterialsTab;
